import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, RotateCcw, Sparkles, Trophy, X } from "lucide-react";
import confetti from "canvas-confetti";
import { QUIZ } from "@/data/content";
import { SectionHead } from "@/components/Reveal";
import Parallax from "@/components/Parallax";

const LETTERS = ["A", "B", "C", "D"];

const fireConfetti = (big) => {
  const colors = ["#D4AF37", "#8B0000", "#1B4D3E", "#FDFBF7"];
  confetti({ particleCount: big ? 180 : 90, spread: big ? 90 : 70, origin: { y: 0.6 }, colors, zIndex: 9999 });
  if (big) {
    setTimeout(() => confetti({ particleCount: 120, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors, zIndex: 9999 }), 250);
    setTimeout(() => confetti({ particleCount: 120, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors, zIndex: 9999 }), 450);
  }
};

export default function Quiz() {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [finalScore, setFinalScore] = useState(0);

  const q = QUIZ[idx];
  const progress = ((done ? QUIZ.length : idx) / QUIZ.length) * 100;

  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const correct = i === q.answer;
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      if (idx + 1 < QUIZ.length) {
        setIdx(idx + 1);
        setPicked(null);
      } else {
        const final = score + (correct ? 1 : 0);
        setFinalScore(final);
        setDone(true);
        fireConfetti(final >= 4);
      }
    }, 1150);
  };

  const restart = () => {
    setIdx(0);
    setPicked(null);
    setScore(0);
    setDone(false);
  };

  const tier =
    finalScore === 5 ? "True Dosco — flawless. The Rose Bowl applauds you."
      : finalScore === 4 ? "Outstanding — you clearly know your Chandbagh lore."
        : finalScore >= 3 ? "A solid showing — the archives approve."
          : "Time for a refresher walk around the Quad.";

  return (
    <section id="quiz" className="relative py-24 lg:py-32">
      <Parallax speed={80}>
        <div className="pointer-events-none absolute right-0 top-1/3 h-[400px] w-[400px] rounded-full bg-crimson/10 blur-[130px]" />
      </Parallax>
      <div className="mx-auto max-w-4xl px-6">
        <SectionHead
          eyebrow="Doon Lore Trivia"
          title="How Well Do You Know Doon Lore?"
          sub="Five questions, ninety-one years of history. Score big and earn your place on the Quad."
        />

        <div className="glass relative overflow-hidden rounded-3xl p-6 sm:p-10" data-testid="quiz-card">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35 }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-[0.3em] text-gold" data-testid="quiz-progress">QUESTION {idx + 1} / {QUIZ.length}</span>
                  <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-soft">
                    <Trophy className="h-3.5 w-3.5 text-gold" /> SCORE {score}
                  </span>
                </div>
                <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-line/15">
                  <motion.div className="h-full bg-gold" animate={{ width: `${Math.max(progress, 4)}%` }} transition={{ duration: 0.5 }} data-testid="quiz-progress-bar" />
                </div>

                <h3 className="mt-8 font-display text-xl font-bold leading-snug text-fg sm:text-2xl" data-testid="quiz-question">{q.q}</h3>

                <div className="mt-7 grid gap-3 sm:grid-cols-2">
                  {q.options.map((opt, i) => {
                    const isCorrect = picked !== null && i === q.answer;
                    const isWrong = picked === i && i !== q.answer;
                    return (
                      <motion.button
                        key={i}
                        onClick={() => pick(i)}
                        whileHover={picked === null ? { y: -3, scale: 1.01 } : {}}
                        whileTap={picked === null ? { scale: 0.97 } : {}}
                        transition={{ type: "spring", stiffness: 300, damping: 18 }}
                        data-testid={`quiz-option-${i}`}
                        animate={isWrong ? { x: [0, -6, 6, -4, 0] } : {}}
                        className={`flex items-center gap-3 rounded-2xl border p-4 text-left text-sm transition-colors duration-300 ${
                          isCorrect
                            ? "border-green-400 bg-green-400/10 text-fg"
                            : isWrong
                              ? "border-red-400 bg-red-400/10 text-fg"
                              : "border-line/20 text-fg hover:-translate-y-0.5 hover:border-gold/50"
                        }`}
                      >
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-mono text-xs ${isCorrect ? "bg-green-400 text-navy" : isWrong ? "bg-red-400 text-navy" : "bg-panel2 text-gold"}`}>
                          {isCorrect ? <Check className="h-4 w-4" /> : isWrong ? <X className="h-4 w-4" /> : LETTERS[i]}
                        </span>
                        {opt}
                      </motion.button>
                    );
                  })}
                </div>

                <AnimatePresence>
                  {picked !== null && (
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 rounded-2xl border-l-2 border-gold bg-panel/60 p-4 font-serif2 text-[15px] italic text-fg/85"
                      data-testid="quiz-fact"
                    >
                      {q.fact}
                    </motion.p>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-6 text-center">
                <div className="relative flex h-36 w-36 items-center justify-center">
                  <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full -rotate-90">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(212,175,55,0.15)" strokeWidth="8" />
                    <motion.circle
                      cx="60" cy="60" r="52" fill="none" stroke="#D4AF37" strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 52}
                      initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - finalScore / QUIZ.length) }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </svg>
                  <span className="font-display text-3xl font-bold text-gold" data-testid="quiz-final-score">{finalScore}/5</span>
                </div>
                <Sparkles className="mt-6 h-6 w-6 text-gold" />
                <h3 className="mt-3 font-display text-2xl font-bold text-fg" data-testid="quiz-tier">{tier}</h3>
                <button
                  onClick={restart}
                  data-testid="quiz-restart"
                  className="mt-8 flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 font-display text-xs font-bold tracking-[0.2em] text-navy transition-transform hover:scale-105"
                >
                  <RotateCcw className="h-4 w-4" /> PLAY AGAIN
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
